import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Home, FileText, MessageSquare,
  Calendar, MapPin, User, Settings,
  Clock, ClipboardCheck
} from 'lucide-react';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import { hoaProfile } from '../../lib/hoaProfile';
import { Card, SectionHeader } from '../ui';
import { motion } from 'framer-motion';
import { logEvent } from '../../lib/activityLogger';
import { supabase } from '../../lib/supabase';
import { messagingService } from '../../services/messagingService';
import MemberLibrary from '../member/MemberLibrary';
import EventManagement from '../admin/EventManagement';
import CommunityDirectory from '../common/CommunityDirectory';
import MessageInbox from '../messages/MessageInbox';
import ACCReview from '../common/ACCReview';
import { canSeeACCReview } from '../../lib/accVisibility';
import {
  formatCurrency,
  getCurrentUserBalance,
  getNextVisibleEvent,
  getVisibleDocumentCount,
} from '../../services/balanceService';

const MemberPortal = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';
  const { tabVisibility, user, impersonating } = useAuth();
  const { hoa } = useHOA();
  const [notifications, setNotifications] = useState({});
  const notificationFetchInFlightRef = React.useRef(false);

  React.useEffect(() => {
    if (!user || !hoa?.id) return;
    const fetchNotifications = async () => {
      if (impersonating || notificationFetchInFlightRef.current) return;
      notificationFetchInFlightRef.current = true;
      try {
        const count = await messagingService.getUnreadThreadCount(user, user.portalRole || 'member', hoa.id);
        setNotifications({ messages: count });
      } catch (error) {
        console.warn('Unread message refresh failed:', error?.message || error);
      } finally {
        notificationFetchInFlightRef.current = false;
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000);

    // Listen for manual read events from MessageInbox to instantly update
    const handleRead = () => fetchNotifications();
    window.addEventListener('messages_read', handleRead);

    return () => {
      clearInterval(interval);
      window.removeEventListener('messages_read', handleRead);
    };
  }, [user, impersonating, hoa?.id]);

  const allTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'messages', label: 'Inbox', icon: MessageSquare },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'directory', label: 'Directory', icon: MapPin },
    ...(canSeeACCReview(user) ? [{ id: 'accReview', label: 'ACC Review', icon: ClipboardCheck }] : []),
    { id: 'profile', label: 'My Profile', icon: User },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const tabs = allTabs.filter(tab => {
    const vis = tabVisibility?.member;
    return !vis || vis[tab.id] === undefined || vis[tab.id];
  });

  const content = {
    dashboard: <MemberDashboard onTabChange={(t) => setSearchParams({ tab: t })} />,
    documents: <MemberLibrary onOpenAISearch={() => {}} />,
    messages: <MessageInbox />,
    events: <CommunityEvents />,
    directory: <CommunityDirectory />,
    accReview: <ACCReview />,
    profile: <MemberProfileV2 />,
    settings: <MemberSettings />,
  };

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  return (
    <PortalLayout title="Member Portal" tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} userRole="member" notifications={notifications}>
      {content[activeTab]}
    </PortalLayout>
  );
};



/* ── DASHBOARD ─────────────────────────────────────────── */
const MemberDashboard = ({ onTabChange }) => {
  const { user, isLoading: authLoading } = useAuth();
  const { hoa, isLoading: hoaLoading } = useHOA();
  const firstName = user?.first_name || user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Neighbor';
  const [inboxPreview, setInboxPreview] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [readMap, setReadMap] = useState(new Map());
  const [documentCount, setDocumentCount] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState('');

  React.useEffect(() => {
    if (authLoading || hoaLoading) {
      setDashboardLoading(true);
      return;
    }

    if (!supabase || !user?.id || !hoa?.id) {
      setDashboardLoading(false);
      setDashboardError('Portal data is still unavailable. Refresh or sign in again.');
      return;
    }

    let mounted = true;

    const fetchDashboardData = async () => {
      setDashboardLoading(true);
      setDashboardError('');
      try {
        const [docCount, event, balance, threadsResult, count, map] = await Promise.all([
          getVisibleDocumentCount({ hoaId: hoa.id, role: user.portalRole || 'member' }),
          getNextVisibleEvent({ hoaId: hoa.id, role: user.portalRole || 'member' }),
          getCurrentUserBalance({ hoaId: hoa.id, userId: user.id }),
          supabase
            .from('message_threads')
            .select('id, subject, type, created_at, created_by, updated_at, acc_details')
            .eq('hoa_id', hoa.id)
            .order('updated_at', { ascending: false })
            .limit(10),
          messagingService.getUnreadThreadCount(user, user.portalRole || 'member', hoa.id),
          messagingService.getReadThreadIds(user.id),
        ]);

        if (!mounted) return;
        if (threadsResult.error) throw threadsResult.error;

        const relevant = (threadsResult.data || []).filter(t => {
          if (t.type === 'announcement') return true;
          if (t.acc_details?.is_welcome && t.acc_details?.target_user_id === user.id) return true;
          if (t.created_by === user.id) return true;
          return false;
        });

        setDocumentCount(docCount);
        setNextEvent(event);
        setBalanceSummary(balance);
        setInboxPreview(relevant.slice(0, 3));
        setUnreadCount(count || 0);
        setReadMap(map);
      } catch (error) {
        console.error('Member dashboard load error:', error);
        if (!mounted) return;
        setDashboardError(error?.message || 'Dashboard data could not be loaded.');
      } finally {
        if (mounted) setDashboardLoading(false);
      }
    };

    fetchDashboardData();
    return () => {
      mounted = false;
    };
  }, [authLoading, hoaLoading, user, hoa?.id]);

  const formatEventDate = (value) => {
    if (!value) return 'Date not scheduled';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Date not scheduled';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Denver',
    });
  };

  const formatAsOfDate = (value) => {
    if (!value) return '';
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleTileKeyDown = (tab) => (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onTabChange(tab);
  };

  const renderInboxPreview = () => {
    if (dashboardLoading) {
      return (
        <div className="flex items-center justify-center py-10">
          <span className="material-symbols-outlined text-3xl text-secondary/40 animate-spin">progress_activity</span>
        </div>
      );
    }

    if (inboxPreview.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <span className="material-symbols-outlined text-3xl text-slate-300 mb-2">mark_email_read</span>
          <p className="text-sm font-medium text-slate-400">No messages yet</p>
          <p className="text-xs text-slate-300 mt-1">Messages from the board will appear here</p>
        </div>
      );
    }

    return inboxPreview.map(thread => {
      const lastRead = readMap.get(thread.id);
      const updatedMs = new Date(thread.updated_at).getTime();
      const isUnread = !lastRead || updatedMs > lastRead;
      const isWelcome = thread.acc_details?.is_welcome === true;
      // Welcome threads are owned by the recipient (thread.created_by === recipient),
      // but the actual message author is the admin who sent it (acc_details.sender_name).
      // Comparing thread.created_by to the current user would incorrectly show "You".
      const isFromBoard = isWelcome ? true : thread.created_by !== user.id;
      const senderLabel = thread.type === 'announcement'
        ? 'HOA Announcement'
        : isWelcome
          ? `From: ${thread.acc_details?.sender_name || 'HOA Team'}`
          : isFromBoard ? hoaProfile(hoa).boardSenderLabel : 'You';
      return (
        <div
          key={thread.id}
          className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
            isUnread ? 'bg-secondary/5 border border-secondary/20' : 'hover:bg-main-bg border border-transparent'
          }`}
        >
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
            isUnread ? 'bg-secondary animate-amber-pulse' : 'bg-transparent'
          }`} />
          <div className="flex-1 min-w-0">
            <p className={`text-xs truncate ${
              isUnread ? 'font-bold text-primary-dark' : 'font-medium text-slate-500'
            }`}>{senderLabel}</p>
            <p className="text-[11px] text-slate-400 truncate mt-0.5">{thread.subject}</p>
          </div>
          {isUnread && (
            <span className="text-[9px] font-bold text-secondary uppercase tracking-wider bg-secondary/10 px-2 py-0.5 rounded-full flex-shrink-0">New</span>
          )}
        </div>
      );
    });
  };

  return (
    <div className="space-y-8">
      <section className="relative h-auto min-h-[230px] py-6 md:py-0 md:h-[230px] rounded-3xl overflow-hidden warm-tactile-shadow">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/hero-bg.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="relative h-full flex flex-col justify-center px-6 md:px-12 max-w-2xl">
          <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-2">
            {hoaProfile(hoa).shortName} | Member Portal
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-2">
            Hi, {firstName}
          </h2>
          <p className="text-white/80 text-sm font-medium leading-relaxed">
            Welcome back to your mountain community.
          </p>
        </div>
      </section>

      <section className="bg-section-bg p-6 rounded-[2.5rem] space-y-6">
        {dashboardError && (
          <div className="bg-white rounded-2xl p-4 border border-red-100 text-red-700 text-sm font-bold">
            {dashboardError}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          <div
            role="button"
            tabIndex={0}
            onClick={() => onTabChange('documents')}
            onKeyDown={handleTileKeyDown('documents')}
            className="bg-[#1D2B20] rounded-2xl p-5 text-white flex flex-col warm-tactile-shadow relative overflow-hidden group min-h-[220px] xl:aspect-square cursor-pointer focus:outline-none focus:ring-2 focus:ring-secondary/40"
          >
            <div className="absolute -right-16 -bottom-16 w-56 h-56 bg-white/5 rounded-full blur-3xl group-hover:bg-white/10 transition-colors duration-500" />
            <div className="relative z-10">
              <div className="w-11 h-11 bg-white/10 rounded-2xl flex items-center justify-center mb-5 border border-white/10">
                <span className="material-symbols-outlined text-2xl text-secondary">folder_open</span>
              </div>
              <h5 className="text-xl font-bold mb-3 leading-tight">
                <span className="block">Documents</span>
                <span className="block">& AI Search</span>
              </h5>
              <p className="text-white/75 leading-relaxed font-medium text-sm">
                CC&Rs, bylaws, meeting minutes and all {dashboardLoading || documentCount === null ? '...' : documentCount} documents - verified and searchable.
              </p>
            </div>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => onTabChange('events')}
            onKeyDown={handleTileKeyDown('events')}
            className="bg-[#DFE7D8] rounded-2xl p-5 flex flex-col justify-between warm-tactile-shadow border border-emerald-300/20 min-h-[220px] xl:aspect-square cursor-pointer group focus:outline-none focus:ring-2 focus:ring-secondary/40"
          >
            <div>
              <div className="w-11 h-11 bg-white/90 rounded-2xl flex items-center justify-center mb-5 border border-white">
                <span className="material-symbols-outlined text-2xl text-secondary">calendar_today</span>
              </div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Next Event</p>
              {dashboardLoading ? (
                <p className="text-lg font-bold text-primary-dark">Loading...</p>
              ) : nextEvent ? (
                <>
                  <h5 className="text-lg font-bold text-primary-dark leading-tight mb-3">{nextEvent.title}</h5>
                  <p className="text-sm font-medium text-slate-600 leading-relaxed">
                    {formatEventDate(nextEvent.scheduled_at)}
                    {nextEvent.location ? ` | ${nextEvent.location}` : ''}
                  </p>
                </>
              ) : (
                <>
                  <h5 className="text-lg font-bold text-primary-dark leading-tight mb-3">No event scheduled</h5>
                  <p className="text-sm font-medium text-slate-600 leading-relaxed">Upcoming meetings will appear here once your manager or the board adds them.</p>
                </>
              )}
            </div>

          </div>

          {(() => {
            const hasBalance = balanceSummary?.hasBalanceData;
            const isPaidInFull = hasBalance && (balanceSummary?.totalCents || 0) <= 0;
            const hasAmountDue = hasBalance && (balanceSummary?.totalCents || 0) > 0;
            const isPastDue = hasBalance && balanceSummary?.hasPastDue31Plus;
            const tileBorder = isPastDue
              ? 'border border-red-200'
              : isPaidInFull
                ? 'border border-emerald-200'
                : 'border border-amber-100/30';
            const iconBg = isPastDue ? 'bg-red-50' : isPaidInFull ? 'bg-emerald-50' : 'bg-section-bg';
            const iconColor = isPastDue ? 'text-red-600' : isPaidInFull ? 'text-emerald-600' : 'text-secondary';
            const amountColor = isPastDue ? 'text-red-700' : 'text-primary-dark';
            return (
              <div className={`bg-white rounded-2xl p-5 flex flex-col justify-between warm-tactile-shadow ${tileBorder} min-h-[220px]`}>
                <div>
                  <div className={`w-11 h-11 ${iconBg} rounded-2xl flex items-center justify-center mb-5 warm-tactile-shadow`}>
                    <span className={`material-symbols-outlined text-2xl ${iconColor}`}>
                      {isPaidInFull ? 'check_circle' : 'payments'}
                    </span>
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Current Balance</p>
                  {dashboardLoading ? (
                    <h5 className="text-2xl font-extrabold text-primary-dark tracking-tight mb-3">...</h5>
                  ) : isPaidInFull ? (
                    <h5 className="text-2xl font-extrabold text-emerald-700 tracking-tight mb-3">Paid in Full</h5>
                  ) : (
                    <h5 className={`text-2xl font-extrabold ${amountColor} tracking-tight mb-3`}>
                      {hasBalance ? formatCurrency(balanceSummary.total) : '$-.--'}
                    </h5>
                  )}
                  {hasBalance && isPastDue && (
                    <p className="text-xs font-semibold text-red-700 leading-relaxed mb-1">
                      Past due 31+ days: {formatCurrency((balanceSummary.agingPastDue31PlusCents || 0) / 100)}
                      {balanceSummary.agingPastDue1To30Cents > 0
                        ? ` · 1–30 days: ${formatCurrency(balanceSummary.agingPastDue1To30Cents / 100)}`
                        : ''}
                    </p>
                  )}
                  {hasBalance && balanceSummary?.asOfDate ? (
                    <p className="text-sm font-medium text-slate-500 leading-relaxed">
                      Balance as of {formatAsOfDate(balanceSummary.asOfDate)}.
                    </p>
                  ) : !dashboardLoading ? (
                    <p className="text-sm font-medium text-slate-500 leading-relaxed">
                      No balance information available yet. Contact your property manager for details.
                    </p>
                  ) : null}
                </div>
                <div>
                  {balanceSummary?.paymentPortalUrl && hasAmountDue && (
                    <a
                      href={balanceSummary.paymentPortalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 inline-flex items-center gap-2 bg-secondary text-white px-5 py-2.5 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all"
                    >
                      Pay Now
                      <span className="material-symbols-outlined text-lg">arrow_forward</span>
                    </a>
                  )}
                  {balanceSummary?.paymentPortalUrl && isPaidInFull && (
                    <a
                      href={balanceSummary.paymentPortalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 inline-flex items-center gap-2 font-bold text-secondary hover:text-primary-dark transition-all group/btn"
                    >
                      Open Payment Portal
                      <span className="material-symbols-outlined text-xl transition-transform group-hover/btn:translate-x-2">arrow_forward</span>
                    </a>
                  )}
                  {hasBalance && (
                    <p className="mt-2 text-[10px] font-medium text-slate-400 leading-snug">
                      Recent payments may take 5–7 business days to reflect.
                    </p>
                  )}
                </div>
              </div>
            );
          })()}

          <div
            className="bg-white rounded-2xl p-5 warm-tactile-shadow border border-slate-200/50 cursor-pointer group hover:border-secondary/40 transition-all min-h-[220px] xl:aspect-square flex flex-col"
            onClick={() => onTabChange('messages')}
            onKeyDown={handleTileKeyDown('messages')}
            role="button"
            tabIndex={0}
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-secondary/10 rounded-xl flex items-center justify-center relative">
                  <span className="material-symbols-outlined text-2xl text-secondary">chat_bubble</span>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-secondary text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-amber-pulse">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <div>
                  <h5 className="text-lg font-bold text-primary-dark leading-tight">Inbox</h5>
                  {unreadCount > 0 && (
                    <p className="text-xs font-semibold text-secondary">{unreadCount} unread message{unreadCount > 1 ? 's' : ''}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-1 flex-1">
              {renderInboxPreview()}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
/* ── DOCUMENTS REMOVED ─────────────────────────────────── */
/* (Handled by DocumentManagement component) */

/* ── EVENTS ───────────────────────────────────────────── */
const CommunityEvents = () => <EventManagement />;

const MemberProfileV2 = () => {
  const { user } = useAuth();
  const initialProfile = React.useCallback(() => ({
    first_name: user?.first_name || user?.name?.split(' ')[0] || '',
    last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    address: user?.address || '',
    phone: user?.phone || '',
  }), [user]);
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState(initialProfile);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const editableFields = [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'phone', label: 'Phone Number' },
  ];
  const readOnlyFields = [
    { key: 'email', label: 'Email Address', icon: 'mail' },
    { key: 'address', label: 'Property Address', icon: 'home' },
  ];
  const editableInputClass = 'w-full px-4 py-3 bg-white border-2 border-amber-300/80 rounded-2xl text-sm text-primary-dark outline-none focus:border-secondary focus:ring-4 focus:ring-secondary/15 hover:border-secondary/70 transition-all';

  React.useEffect(() => {
    setProfile(initialProfile());
  }, [initialProfile]);

  const handleSave = async () => {
    const firstName = profile.first_name.trim();
    const lastName = profile.last_name.trim();
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    if (!firstName || !lastName) {
      setSaveError('First and last name are required.');
      return;
    }

    setIsSaving(true);
    setSaveError('');
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          full_name: fullName,
          phone: profile.phone.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
      logEvent('profile_updated', { fields_changed: ['first_name', 'last_name', 'full_name', 'phone'] });
      setEditing(false);
    } catch (error) {
      console.error('Member profile save failed:', error);
      setSaveError(error.message || 'Profile could not be saved.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="bg-section-bg p-6 rounded-[2.5rem] border border-amber-100/30 space-y-6 max-w-3xl">
      <div className="bg-white rounded-2xl p-6 warm-tactile-shadow border-2 border-amber-300/70">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
          <SectionHeader icon={User} title="My Profile" />
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-secondary hover:brightness-110 active:scale-95 transition-all text-white text-sm font-bold rounded-full"
            >
              <span className="material-symbols-outlined text-[18px]">edit</span>
              Edit Profile
            </button>
          )}
        </div>

        {saved && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-2xl flex items-center gap-2 text-amber-700 text-sm font-bold">
            <Clock className="w-4 h-4" /> Profile saved.
          </motion.div>
        )}

        {saveError && (
          <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-2xl text-rose-700 text-sm font-bold">
            {saveError}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_0.9fr] gap-5">
          <div className="bg-section-bg rounded-2xl border-2 border-amber-300/70 p-5 space-y-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Editable Profile</p>
              <p className="text-sm font-medium text-slate-600">Name and phone number can be updated here.</p>
            </div>
            {editableFields.map((field) => (
              <div key={field.key} className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest">{field.label}</label>
                {editing ? (
                  <input
                    type="text"
                    value={profile[field.key]}
                    onChange={(event) => setProfile((prev) => ({ ...prev, [field.key]: event.target.value }))}
                    className={editableInputClass}
                  />
                ) : (
                  <div className="px-4 py-3 bg-white rounded-2xl border-2 border-amber-300/80 text-sm font-bold text-primary-dark min-h-[46px] flex items-center">
                    {profile[field.key] || '-'}
                  </div>
                )}
              </div>
            ))}

            {editing ? (
              <div className="flex flex-wrap gap-3 pt-2">
                <button disabled={isSaving} onClick={handleSave} className="px-5 py-2.5 bg-secondary hover:brightness-110 active:scale-95 transition-all text-white text-sm font-bold rounded-full disabled:opacity-50">
                  {isSaving ? 'Saving...' : 'Save Profile'}
                </button>
                <button onClick={() => { setEditing(false); setSaveError(''); setProfile(initialProfile()); }} className="px-5 py-2.5 bg-white hover:bg-main-bg text-slate-700 text-sm font-bold rounded-full border-2 border-amber-300/80 transition-all">Cancel</button>
              </div>
            ) : null}
          </div>

          <div className="bg-main-bg rounded-2xl border-2 border-amber-300/70 p-5 space-y-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Account Details</p>
              <p className="text-sm font-medium text-slate-600">These are read-only in the homeowner profile.</p>
            </div>
            {readOnlyFields.map((field) => (
              <div key={field.key} className="rounded-2xl bg-section-bg/70 border-2 border-dashed border-amber-400/70 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-symbols-outlined text-secondary text-[18px]">{field.icon}</span>
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{field.label}</span>
                </div>
                <div className="text-sm font-bold text-slate-500 break-words">{profile[field.key] || '-'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// eslint-disable-next-line no-unused-vars
const MemberProfile = () => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    address: user?.address || '',
    phone: user?.phone || '',
  });
  const [pendingChanges, setPendingChanges] = useState(null);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    // Determine which fields changed
    const changes = {};
    if (profile.name !== (user?.name || '')) changes.name = profile.name;
    if (profile.phone !== (user?.phone || '')) changes.phone = profile.phone;
    if (profile.address !== (user?.address || '')) changes.address = profile.address;
    if (Object.keys(changes).length > 0) {
      setPendingChanges(changes);
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
      logEvent('profile_updated', { fields_changed: Object.keys(changes) });
    }
    setEditing(false);
  };

  const roleLabels = { admin: 'Administrator', management: 'Management', board: 'Board Member', member: 'Homeowner' };
  const roleBadge = { admin: 'bg-amber-100 text-amber-800 border-amber-200', management: 'bg-forest-100 text-forest-800 border-forest-200', board: 'bg-forest-100 text-forest-800 border-forest-200', member: 'bg-slate-100 text-slate-700 border-slate-200' };

  const fields = [
    { key: 'name', label: 'Full Name', editable: true },
    { key: 'email', label: 'Email Address', editable: false },
    { key: 'address', label: 'Property Address', editable: true },
    { key: 'phone', label: 'Phone Number', editable: true },
  ];

  return (
    <div className="space-y-5">
      <Card>
        <SectionHeader icon={User} title="My Profile" />

        {saved && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2 text-amber-700 text-sm font-medium">
            <Clock className="w-4 h-4" /> Changes submitted for admin approval. You'll receive a notification once reviewed.
          </motion.div>
        )}

        {pendingChanges && !saved && (
          <div className="mb-4 p-3 bg-forest-50 border border-forest-200 rounded-xl flex items-center gap-2 text-forest-700 text-sm font-medium">
            <Clock className="w-4 h-4" /> You have pending profile changes awaiting admin approval.
          </div>
        )}

        <div className="space-y-3 max-w-lg">
          {fields.map((f) => (
            <div key={f.key} className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200 shadow-sm">
              <span className="text-slate-500 text-sm w-36 flex-shrink-0">{f.label}</span>
              {editing && f.editable ? (
                <input
                  type="text"
                  value={profile[f.key]}
                  onChange={e => setProfile(p => ({ ...p, [f.key]: e.target.value }))}
                  className="flex-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 outline-none focus:border-forest-400 focus:ring-1 focus:ring-forest-100 transition-all"
                />
              ) : (
                <div className="text-right">
                  <span className="text-slate-900 text-sm font-medium">{profile[f.key] || '—'}</span>
                  {pendingChanges?.[f.key] && (
                    <div className="text-xs text-amber-600 mt-0.5">⏳ Pending: {pendingChanges[f.key]}</div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Legacy role badge (unused) */}
          <div className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200 shadow-sm">
            <span className="text-slate-500 text-sm w-36 flex-shrink-0">Legacy Role</span>
            <span className={`px-2.5 py-0.5 text-xs rounded-full border font-medium ${roleBadge[user?.role] || roleBadge.member}`}>
              {roleLabels[user?.role] || 'Homeowner'}
            </span>
          </div>

          <div className="flex gap-3 mt-2">
            {editing ? (
              <>
                <button onClick={handleSave} className="px-4 py-2.5 bg-secondary hover:brightness-110 active:scale-95 transition-all text-white text-sm font-semibold rounded-xl">Submit for Approval</button>
                <button onClick={() => { setEditing(false); setProfile({ name: user?.name || '', email: user?.email || '', address: user?.address || '', phone: user?.phone || '' }); }} className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-slate-700 text-sm rounded-xl transition-colors">Cancel</button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} className="px-4 py-2.5 bg-secondary hover:brightness-110 active:scale-95 transition-all text-white text-sm font-semibold rounded-xl">Edit Profile</button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

/* ── SETTINGS ─────────────────────────────────────────── */
const MemberSettings = () => (
  <Card>
    <SectionHeader icon={Settings} title="Notification Settings" />
    <div className="space-y-3 max-w-md">
      {[
        { label: 'Email announcements', sub: 'Community-wide notices', on: true },
        { label: 'Payment reminders', sub: 'Dues collection window alerts', on: true },
        { label: 'Meeting notifications', sub: 'Board meeting reminders', on: true },
        { label: 'ACC response alerts', sub: 'Updates on your requests', on: false },
      ].map((s, i) => (
        <div key={i} className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200 shadow-sm">
          <div>
            <div className="text-slate-900 text-sm font-medium">{s.label}</div>
            <div className="text-slate-400 text-xs">{s.sub}</div>
          </div>
          <div className={`w-10 h-5.5 rounded-full relative cursor-pointer ${s.on ? 'bg-secondary' : 'bg-slate-200'} transition-colors`}>
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${s.on ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </div>
        </div>
      ))}
    </div>
  </Card>
);

export default MemberPortal;
